import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  EventWeather,
  getEventById,
  getEventWeather,
  IEventItem,
} from "../../services/eventsApi";

const formatDateTime = (value?: string) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const dateKey = (value?: string) => String(value || "").split("T")[0];

const weatherIcon = (condition?: string) => {
  const value = String(condition || "").toLowerCase();
  if (value.includes("thunder")) return "⛈️";
  if (value.includes("snow") || value.includes("ice")) return "❄️";
  if (value.includes("rain") || value.includes("drizzle")) return "🌧️";
  if (value.includes("overcast")) return "☁️";
  if (value.includes("cloud")) return "⛅";
  if (value.includes("clear") || value.includes("sun")) return "☀️";
  if (value.includes("fog") || value.includes("mist")) return "🌫️";
  return "🌤️";
};

export default function EventInfoPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<IEventItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [weather, setWeather] = useState<EventWeather[]>([]);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState("");
  const [weatherRefreshKey, setWeatherRefreshKey] = useState(0);

  useEffect(() => {
    if (!eventId) {
      setError("Event ID is missing.");
      setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      try {
        const data = await getEventById(eventId);
        if (active) setEvent(data);
      } catch (requestError: any) {
        if (active)
          setError(
            requestError?.response?.data?.message ||
              requestError?.message ||
              "Could not load this event.",
          );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [eventId]);

  useEffect(() => {
    if (!eventId || !event) return;
    let active = true;
    const filterToEventDates = (rows: EventWeather[]) => {
      const start = dateKey(event.startDateTime);
      const end = dateKey(event.endDateTime || event.startDateTime);
      return rows.filter((row) => {
        const day = dateKey(row.date);
        return day && (!start || day >= start) && (!end || day <= end);
      });
    };

    const existing = Array.isArray(event.weather)
      ? event.weather
      : event.weather
        ? [event.weather]
        : [];
    setWeather(filterToEventDates(existing));

    (async () => {
      try {
        setWeatherLoading(true);
        setWeatherError("");
        const response = await getEventWeather(eventId);
        if (active) {
          setWeather(
            filterToEventDates(
              Array.isArray(response?.weather) ? response.weather : [],
            ),
          );
        }
      } catch (requestError: any) {
        if (active) {
          setWeatherError(
            requestError?.response?.data?.message ||
              requestError?.message ||
              "Weather is not available for this event.",
          );
        }
      } finally {
        if (active) setWeatherLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [event, eventId, weatherRefreshKey]);

  const eventStatus = String(event?.status || "").toLowerCase();
  const isCanceled = eventStatus === "canceled" || eventStatus === "cancelled";

  return (
    <div className="min-h-[calc(100vh-56px)] bg-slate-50/90 px-4 py-8 md:px-8">
      <div className="mx-auto max-w-4xl">
        <button
          type="button"
          onClick={() => navigate("/events")}
          className="mb-5 text-sm font-semibold text-blue-600 hover:text-blue-700"
        >
          ← Back to events
        </button>
        {loading ? (
          <div
            className="flex min-h-96 flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm"
            role="status"
            aria-live="polite"
          >
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
            <p className="mt-4 text-sm font-semibold text-slate-700">
              Loading event details…
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Fetching the latest event information
            </p>
          </div>
        ) : error || !event ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
            {error || "Event not found."}
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-8 text-white md:px-8">
              <p className="text-sm font-semibold uppercase tracking-wide text-blue-100">
                Event information
              </p>
              <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <h1 className="text-3xl font-bold tracking-tight">
                  {event.title}
                </h1>
                <span
                  className={`w-fit rounded-full px-3 py-1 text-sm font-semibold capitalize ring-1 ${isCanceled ? "bg-red-500 ring-red-300" : "bg-white/20 ring-white/30"}`}
                >
                  {isCanceled
                    ? "canceled"
                    : String(event.status || "upcoming").toLowerCase()}
                </span>
              </div>
              {isCanceled && event.cancellationReason ? (
                <p className="mt-3 text-sm text-blue-50">
                  <span className="font-semibold">Cancellation reason:</span>{" "}
                  {event.cancellationReason}
                </p>
              ) : null}
            </div>
            <div className="grid gap-6 p-6 md:grid-cols-2 md:p-8">
              <section className="rounded-xl border border-slate-200 p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Schedule
                </h2>
                <dl className="mt-4 space-y-4">
                  <div>
                    <dt className="text-xs font-medium text-slate-500">
                      Starts
                    </dt>
                    <dd className="mt-1 text-sm font-semibold text-slate-900">
                      {formatDateTime(event.startDateTime)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-slate-500">Ends</dt>
                    <dd className="mt-1 text-sm font-semibold text-slate-900">
                      {formatDateTime(event.endDateTime)}
                    </dd>
                  </div>
                </dl>
              </section>
              <section className="rounded-xl border border-slate-200 p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Location
                </h2>
                <p className="mt-4 text-sm font-semibold text-slate-900">
                  {event.location || "No location added"}
                </p>
                {event.location && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-block text-sm font-semibold text-blue-600 hover:text-blue-700"
                  >
                    Open in Google Maps →
                  </a>
                )}
              </section>
              <section className="rounded-xl border border-slate-200 p-5 md:col-span-2">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Weather
                    </h2>
                    <p className="mt-1 text-xs text-slate-400">
                      Forecast for the event location and dates
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setWeatherRefreshKey((value) => value + 1)}
                    disabled={weatherLoading}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {weatherLoading ? "Refreshing…" : "Refresh"}
                  </button>
                </div>

                {weatherLoading && weather.length === 0 ? (
                  <div
                    className="mt-5 flex items-center gap-3 rounded-lg bg-blue-50 px-4 py-5 text-sm text-blue-700"
                    role="status"
                  >
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
                    Loading weather forecast…
                  </div>
                ) : weather.length > 0 ? (
                  <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {weather.map((forecast, index) => {
                      const temperatureF =
                        forecast.temperatureF ?? forecast.temperature_f;
                      const windMph =
                        typeof forecast.windSpeed === "number"
                          ? forecast.windSpeed * 0.621371
                          : null;
                      return (
                        <article
                          key={`${forecast.date}-${index}`}
                          className="rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 to-blue-50 p-4"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
                                {new Date(
                                  `${dateKey(forecast.date)}T12:00:00`,
                                ).toLocaleDateString(undefined, {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </p>
                              <p className="mt-2 text-3xl font-bold text-slate-900">
                                {typeof temperatureF === "number"
                                  ? `${Math.round(temperatureF)}°F`
                                  : "—"}
                              </p>
                            </div>
                            <span className="text-4xl" aria-hidden="true">
                              {weatherIcon(forecast.condition)}
                            </span>
                          </div>
                          <p className="mt-2 text-sm font-semibold capitalize text-slate-700">
                            {forecast.condition || "Forecast unavailable"}
                          </p>
                          <div className="mt-3 flex gap-4 text-xs text-slate-500">
                            <span>
                              Humidity: {typeof forecast.humidity === "number"
                                ? `${forecast.humidity}%`
                                : "—"}
                            </span>
                            <span>
                              Wind: {windMph === null
                                ? "—"
                                : `${windMph.toFixed(1)} mph`}
                            </span>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-5 rounded-lg bg-slate-50 px-4 py-5 text-sm text-slate-500">
                    {weatherError ||
                      "No forecast is available for these event dates."}
                  </p>
                )}
                {weatherError && weather.length > 0 && (
                  <p className="mt-3 text-xs text-amber-700">
                    Could not refresh the forecast: {weatherError}
                  </p>
                )}
              </section>
              <section className="rounded-xl border border-slate-200 p-5 md:col-span-2">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Description
                </h2>
                <p className="mt-4 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                  {event.description || "No description added."}
                </p>
              </section>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
